/**
 * screens/admin/adminBilling.tsx — manual adjustments and statements (BIL-001
 * §9–§10). Softknack staff only. An adjustment at or above the threshold waits
 * for a SECOND staff member; the requester can never approve their own. The API
 * enforces both; this screen only reflects them.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useSession } from '../../context/sessionContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, DataTable, StatusBadge } from '../../components/platform/primitives';
import { PButton, PModal, PSelect, PTextInput } from '../../components/platform/controls';
import Tabs from '../../components/tabs';
import Alert from '../../components/alert';
import { PermissionDenied } from '../shell/chrome';
import { FS } from '../../utils/platformTokens';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { listAdjustments, createAdjustment, approveAdjustment, rejectAdjustment } from '../../services/platform/platformApi';
import type { Adjustment, AdjustmentInput } from '../../schema/platform/types';
import { StatementDownload } from '../settings/billing';

const REASONS: { value: Adjustment['reasonCode']; label: string }[] = [
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'correction', label: 'Correction' },
  { value: 'promo', label: 'Promotion' },
  { value: 'migration', label: 'Migration' },
];

const NewAdjustment: React.FC<{ open: boolean; onClose: () => void; onDone: () => void }> = ({ open, onClose, onDone }) => {
  const toast = useToast();
  const blank: AdjustmentInput = { orgId: '', kind: 'units', item: '', quantity: 0, reasonCode: 'goodwill', note: '' };
  const [f, setF] = useState<AdjustmentInput>(blank);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setF(blank); setErr(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setSaving(true);
    try {
      const body: AdjustmentInput = f.kind === 'units'
        ? { orgId: f.orgId.trim(), kind: 'units', item: f.item, quantity: f.quantity, reasonCode: f.reasonCode, note: f.note || undefined }
        : { orgId: f.orgId.trim(), kind: 'money', amountMinor: f.amountMinor, reasonCode: f.reasonCode, note: f.note || undefined };
      const a = await createAdjustment(body);
      toast.success(a.status === 'pending_approval' ? 'Sent for a second approval.' : 'Adjustment applied.');
      onDone();
      onClose();
    } catch (e) {
      setErr(getApiErrorMessage(e, "Couldn't create the adjustment."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PModal open={open} onClose={onClose} title="New adjustment"
      footer={<>
        <PButton kind="ghost" onPress={onClose}>Cancel</PButton>
        <PButton kind="accent" loading={saving} disabled={!f.orgId.trim()} onPress={submit}>Create</PButton>
      </>}>
      <View style={{ gap: 14 }}>
        {err && <Alert tone="danger">{err}</Alert>}
        <PTextInput label="Organisation ID" mono value={f.orgId} onChangeText={(v) => setF({ ...f, orgId: v })} />
        <PSelect label="Kind" value={f.kind} onChange={(v) => setF({ ...f, kind: v as AdjustmentInput['kind'] })}
          options={[{ value: 'units', label: 'Units' }, { value: 'money', label: 'Money (wallet)' }]} />
        {f.kind === 'units' ? (
          <>
            <PTextInput label="Item" mono placeholder="whatsapp.credit" value={f.item ?? ''} onChangeText={(v) => setF({ ...f, item: v })} />
            <PTextInput label="Quantity" hint="Negative to take units back." mono value={String(f.quantity ?? 0)}
              onChangeText={(v) => setF({ ...f, quantity: Number(v.replace(/[^0-9-]/g, '')) || 0 })} />
          </>
        ) : (
          <PTextInput label="Amount in minor units" hint="Paise / fils. In the org's billing currency. Negative to debit." mono
            value={String(f.amountMinor ?? 0)} onChangeText={(v) => setF({ ...f, amountMinor: Number(v.replace(/[^0-9-]/g, '')) || 0 })} />
        )}
        <PSelect label="Reason" value={f.reasonCode} onChange={(v) => setF({ ...f, reasonCode: v as AdjustmentInput['reasonCode'] })} options={REASONS} />
        <PTextInput label="Note" hint="Shown on the customer's statement." value={f.note ?? ''} onChangeText={(v) => setF({ ...f, note: v })} />
      </View>
    </PModal>
  );
};

const AdjustmentsTab: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const toast = useToast();
  const { user } = useSession();
  const [status, setStatus] = useState<'' | Adjustment['status']>('pending_approval');
  const [rows, setRows] = useState<Adjustment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setRows(null);
    listAdjustments(status || undefined).then(
      (r) => { setRows(r); setError(null); },
      (e) => { setRows([]); setError(getApiErrorMessage(e, "Couldn't load adjustments.")); },
    );
  }, [status]);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<void>, ok: string) => {
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(getApiErrorMessage(e, 'That was refused.')); }
  };

  return (
    <Panel title="Adjustments" bodyStyle={{ padding: 0 }}
      actions={<>
        <PSelect size="sm" width={170} value={status} onChange={(v) => setStatus(v as typeof status)}
          options={[{ value: 'pending_approval', label: 'Waiting approval' }, { value: 'applied', label: 'Applied' }, { value: 'rejected', label: 'Rejected' }, { value: '', label: 'All' }]} />
        <PButton size="sm" kind="accent" icon="plus" onPress={() => setCreating(true)}>New adjustment</PButton>
      </>}>
      {error && <View style={{ padding: 16 }}><Alert tone="danger">{error}</Alert></View>}
      {rows === null ? <View style={{ padding: 24 }}><ActivityIndicator color={c.accent} /></View> : (
        <DataTable<Adjustment> rows={rows} keyExtractor={(r) => r.id} minWidth={900}
          empty={<Text style={{ color: c.ink3 }}>No adjustments.</Text>}
          columns={[
            { key: 'org', header: 'Organisation', width: 180, render: (r) => r.orgName },
            { key: 'v', header: 'Adjustment', width: 160, align: 'right', render: (r) => <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{r.display}</Text> },
            { key: 'r', header: 'Reason', width: 110, render: (r) => REASONS.find((x) => x.value === r.reasonCode)?.label ?? r.reasonCode },
            { key: 'note', header: 'Note', render: (r) => <Text style={{ color: c.ink2, fontSize: FS.sm }}>{r.note ?? '—'}</Text> },
            { key: 'by', header: 'Requested by', width: 140, render: (r) => r.requestedBy.name },
            { key: 's', header: 'Status', width: 130, render: (r) => <StatusBadge status={r.status} /> },
            {
              key: 'a', header: '', width: 190, align: 'right', render: (r) => {
                if (r.status !== 'pending_approval') return r.approvedBy ? <Text style={{ color: c.ink3, fontSize: FS.sm }}>by {r.approvedBy.name}</Text> : null;
                if (r.requestedBy.id === user?.id) return <Text style={{ color: c.ink3, fontSize: FS.sm }}>Needs a second approver</Text>;
                return (
                  <View style={{ flexDirection: 'row', gap: 6 }}>
                    <PButton size="sm" kind="accent" onPress={() => act(() => approveAdjustment(r.id), 'Approved and applied.')}>Approve</PButton>
                    <PButton size="sm" kind="ghost" onPress={() => act(() => rejectAdjustment(r.id), 'Rejected.')}>Reject</PButton>
                  </View>
                );
              },
            },
          ]} />
      )}
      <NewAdjustment open={creating} onClose={() => setCreating(false)} onDone={load} />
    </Panel>
  );
};

const StatementsTab: React.FC = () => {
  const [orgId, setOrgId] = useState('');
  return (
    <Panel title="Statements" sub="Monthly statement for any organisation: grants, usage, transfers, adjustments, overage.">
      <View style={{ gap: 12 }}>
        <PTextInput label="Organisation ID" mono value={orgId} onChangeText={setOrgId} />
        {orgId.trim().length > 0 && <StatementDownload orgId={orgId.trim()} />}
      </View>
    </Panel>
  );
};

const TABS = [{ id: 'adjustments', label: 'Adjustments' }, { id: 'statements', label: 'Statements' }];

const AdminBilling: React.FC = () => {
  const { isPlatformAdmin } = usePlatform();
  const [tab, setTab] = useState('adjustments');
  if (!isPlatformAdmin) return <PermissionDenied />;
  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1200, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title="Adjustments & statements" sub="Credit or debit an organisation, with a second approver above the threshold." />
      <Tabs tabs={TABS} value={tab} onChange={setTab} style={{ marginBottom: 16 }} />
      {tab === 'adjustments' ? <AdjustmentsTab /> : <StatementsTab />}
    </ScrollView>
  );
};

export default AdminBilling;
